import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import SoftButton from "components/SoftButton";
import { deleteCredential } from "api/CredentialManagementApi";
import {
  setCredentials,
  setDeleteCredentialDialogOpen
} from "redux/actions/portal/credentialManagementActionCreator";
import { CircularProgress } from "@mui/material";
import { getResourceDependenciesWithHop } from "api/ResourceDependenciesApi";
import { usePortalFlows } from "hooks/usePortalFlows";

const CredentialDeleteDialog = ({ onDeleted }) => {
  const { credentials, isDeleteCredentialDialogOpen, selectedCredential } = useSelector(
    (store) => store?.portal?.credentialManagement
  );
  const [deleteCredentialIsLoading, setDeleteCredentialIsLoading] = useState(false);
  const [credentialUsage, setCredentialUsage] = useState({
    flowIds: [],
    fetchedCredentialId: null,
    hasCheckFailed: false,
    isLoading: false
  });
  const { user } = useAuth0();
  const {
    flows,
    handleFetchFlows,
    areFlowsLoaded,
    isLoadingPortalFlows
  } = usePortalFlows(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isDeleteCredentialDialogOpen) return;

    handleFetchFlows();
  }, [isDeleteCredentialDialogOpen]);

  useEffect(() => {
    if (
      !isDeleteCredentialDialogOpen ||
      !user?.stripeId ||
      !selectedCredential?.id
    ) {
      setCredentialUsage({
        flowIds: [],
        fetchedCredentialId: null,
        hasCheckFailed: false,
        isLoading: false
      });
      return;
    }

    let ignore = false;
    const credentialId = selectedCredential.id;

    setCredentialUsage({
      flowIds: [],
      fetchedCredentialId: null,
      hasCheckFailed: false,
      isLoading: true
    });

    getResourceDependenciesWithHop({
      tenantId: user.stripeId,
      sourceId: credentialId,
      direction: "reverse",
      directTargetResourceType: "flow",
      throughTargetResourceType: "connector",
      finalTargetResourceType: "flow",
      targetIdFromContextId: true
    })
      .then((dependenciesResponse) => {
        if (ignore) return;

        const nextFlowIds = Array.isArray(dependenciesResponse?.resultObject)
          ? dependenciesResponse.resultObject.map((dependency) => dependency.target_id)
          : [];

        setCredentialUsage({
          flowIds: [...new Set(nextFlowIds)],
          fetchedCredentialId: credentialId,
          hasCheckFailed: false,
          isLoading: false
        });
      })
      .catch(() => {
        if (ignore) return;

        setCredentialUsage({
          flowIds: [],
          fetchedCredentialId: credentialId,
          hasCheckFailed: true,
          isLoading: false
        });
      });

    return () => {
      ignore = true;
    };
  }, [isDeleteCredentialDialogOpen, user?.stripeId, selectedCredential?.id]);

  const flowsUsingTheCredential = credentialUsage.flowIds
    .map((flowId) => flows.find((flow) => flow.id === flowId))
    .filter(Boolean);
  const isLoadingFlowNames = credentialUsage.flowIds.length > 0 && (!areFlowsLoaded || isLoadingPortalFlows);
  const isCheckingDependencies =
    isDeleteCredentialDialogOpen &&
    (credentialUsage.isLoading ||
      credentialUsage.fetchedCredentialId !== selectedCredential?.id);
  const isCheckingUsage = isCheckingDependencies || isLoadingFlowNames;
  const hasCheckFailed = credentialUsage.hasCheckFailed;
  const isCredentialUsed = flowsUsingTheCredential.length > 0;

  const handleDeleteCredential = async () => {
    if (deleteCredentialIsLoading || isCheckingUsage || hasCheckFailed) return;
    setDeleteCredentialIsLoading(true);

    let deleteCredentialResponse;

    try {
      deleteCredentialResponse = await deleteCredential({
        tenantId: user?.stripeId,
        credentialId: selectedCredential?.id
      });
    } catch {
      toast.error("Failed to delete credential");
      setDeleteCredentialIsLoading(false);
      return;
    }

    if (deleteCredentialResponse.isSuccessful) {
      toast.success(deleteCredentialResponse?.message);
      dispatch(
        setCredentials(
          credentials.filter((credential) => credential?.id !== selectedCredential?.id)
        )
      );
    } else {
      toast.error(deleteCredentialResponse?.message);
      setDeleteCredentialIsLoading(false);
      return;
    }
    setDeleteCredentialIsLoading(false);
    onClose();
    onDeleted?.();
  };

  const onClose = () => {
    dispatch(setDeleteCredentialDialogOpen(false));
  };

  return (
    <Dialog open={isDeleteCredentialDialogOpen} onClose={onClose} fullWidth>
      <DialogTitle>Delete Credential</DialogTitle>
      <DialogContent>
        {selectedCredential?.shared_credentials === true ? (
          <DialogContentText>
            <strong>{selectedCredential?.application?.name}</strong> is currently shared with other
            users. Please unshare the <strong>{selectedCredential?.application?.name}</strong>{" "}
            before proceeding with the deletion.
          </DialogContentText>
        ) : isCheckingUsage ? (
          <DialogContentText
            sx={{ alignItems: "center", display: "flex", gap: "12px" }}
          >
            <CircularProgress size={20} />
            Checking flows using this credential...
          </DialogContentText>
        ) : hasCheckFailed ? (
          <DialogContentText>
            Usage could not be verified for{" "}
            <strong>{selectedCredential?.application?.name}</strong>. Please try again before
            deleting it.
          </DialogContentText>
        ) : isCredentialUsed ? (
          <>
            <DialogContentText sx={{ marginBottom: "1rem" }}>
              <strong>{selectedCredential?.application?.name}</strong> is currently used by
              flows. Remove the credential from the flows below before deleting it.
            </DialogContentText>
            <ul style={{ paddingLeft: "1rem" }}>
              {flowsUsingTheCredential.map((flow) => (
                <li key={flow.id}>
                  <strong>{flow.name}</strong>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <DialogContentText>
            Do you want to delete <strong>{selectedCredential?.application?.name}</strong>{" "}
            credential?
          </DialogContentText>
        )}
      </DialogContent>

      {selectedCredential?.shared_credentials === true || isCredentialUsed || hasCheckFailed ? (
        <DialogActions>
          <SoftButton onClick={onClose} color="primary" variant="outlined">
            Close
          </SoftButton>
        </DialogActions>
      ) : (
        <DialogActions>
          <SoftButton onClick={onClose} color="primary" variant="outlined">
            Cancel
          </SoftButton>
          <SoftButton
            onClick={handleDeleteCredential}
            color="primary"
            disabled={deleteCredentialIsLoading || isCheckingUsage}
          >
            {deleteCredentialIsLoading ? (
              <CircularProgress
                size={24}
                sx={{
                  color: "#fff",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                  zIndex: 1
                }}
              />
            ) : (
              "Delete"
            )}
          </SoftButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CredentialDeleteDialog;
